import {compact, groupBy, objectFromEntries} from '@benchling/js-utils';
import stringify from 'json-stable-stringify';
import _ from 'underscore';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalRole,
  ExperimentalRolesByTypeMapping,
  NULL_SUBROLE_KEY,
  EXPERIMENTAL_SUBROLE_TO_SYMBOL,
} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {capitalize} from 'util/NameUtil';
import {Optional} from 'util/Types';

/**
 * Get display label text for a role.
 * @param primaryRole Experimental primary role
 * @param subrole Experimental subrole
 * @param group Experimental group
 */
export function getRoleLabelText({
  primaryRole,
  subrole,
  group,
}: {
  primaryRole: string;
  subrole?: Optional<string>;
  group?: Optional<number>;
}): string {
  const parts: Array<string> = compact([subrole, primaryRole, group?.toString()]);
  return capitalize(parts.join(' '));
}

/**
 * Gets a mapping of each PrimaryExperimentalRoleType to the unique set of roles from the given array.
 *
 * Includes an entry for every PrimaryExperimentalRoleType.
 * @param roles List of roles
 */
export function getRolesByRoleType(roles: Array<ExperimentalRole>): ExperimentalRolesByTypeMapping {
  const groupedByRoleType = groupBy(roles, (role) => role.primaryRole);
  const groupEntries: Array<
    [PrimaryExperimentalRoleType, null | {[subrole: string]: Array<ExperimentalRole>}]
  > = Object.keys(PrimaryExperimentalRoleType).map((roleType: PrimaryExperimentalRoleType) => {
    const allGroups = groupedByRoleType[roleType] ?? [];
    if (allGroups.length === 0) {
      return [roleType, null];
    }
    const groupedBySubrole = groupBy(allGroups, (role) => role.subrole ?? NULL_SUBROLE_KEY);
    const subRoleEntries: Array<[string, Array<ExperimentalRole>]> = Object.entries(groupedBySubrole).map(
      ([subrole, groups]) => [subrole, _.unique(groups, (role) => stringify(role))]
    );
    return [roleType, objectFromEntries(subRoleEntries)];
  });
  return objectFromEntries(groupEntries) as ExperimentalRolesByTypeMapping;
}

/**
 * Gets a shorthand symbol for the given role.
 * @param subrole Experimental subrole
 * @param group Experimental group
 */
export function getRoleSymbol({
  subrole,
  group,
}: {
  subrole?: Optional<string>;
  group?: Optional<number>;
}): string | null {
  let label = '';
  if (subrole != null && EXPERIMENTAL_SUBROLE_TO_SYMBOL.hasOwnProperty(subrole)) {
    label += EXPERIMENTAL_SUBROLE_TO_SYMBOL[subrole];
  }
  if (group != null) {
    label += group.toString();
  }
  return label.length > 0 ? label : null;
}
